import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { FaTimes, FaVideo, FaCheckCircle } from 'react-icons/fa';

export default function Step5Video({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const [videoUrl, setVideoUrl] = useState(wizardData.intro_video_url || '');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null); // Local preview URL created with URL.createObjectURL
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  // Cleanup: Revoke object URL on unmount or when file changes to prevent memory leaks
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);


  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Cleanup previous preview URL if exists
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl(null);
    }

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setErrors({ video: isArabic ? 'يرجى تحميل ملف فيديو (mp4، mov، إلخ)' : 'Please upload a video file (mp4, mov, etc.)' });
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setErrors({ video: isArabic ? 'يجب أن يكون حجم الملف أقل من 50 ميجابايت' : 'File size must be less than 50MB' });
      return;
    }

    setErrors({});
    setVideoFile(file);

    // Create preview URL using URL.createObjectURL (will be revoked on unmount or file change)
    const previewUrl = URL.createObjectURL(file);
    setVideoPreviewUrl(previewUrl);

    // Check duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = video.duration;
      if (duration > 60) {
        setErrors({ video: isArabic ? 'يجب أن يكون الفيديو 60 ثانية أو أقل' : 'Video must be 60 seconds or less' });
        setVideoFile(null);
        URL.revokeObjectURL(previewUrl);
        setVideoPreviewUrl(null);
      } else {
        // Auto-upload after validation passes
        handleUpload(file);
      }
    };
    video.onerror = () => {
      setErrors({ video: isArabic ? 'ملف فيديو غير صالح' : 'Invalid video file' });
      setVideoFile(null);
      URL.revokeObjectURL(previewUrl);
      setVideoPreviewUrl(null);
    };
    video.src = previewUrl;
  };

  const handleUpload = async (fileToUpload = null) => {
    const file = fileToUpload || videoFile;
    if (!file) {
      setErrors({ video: isArabic ? 'يرجى اختيار ملف فيديو' : 'Please select a video file' });
      return;
    }

    setUploading(true);
    setErrors({});

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `intro-video-${Date.now()}.${fileExt}`;
      const filePath = `teacher-intros/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('teacher-intros')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('teacher-intros')
        .getPublicUrl(filePath);

      const publicUrl = publicUrlData?.publicUrl;
      if (!publicUrl) throw new Error('Failed to get public URL');

      // Cleanup local preview URL after successful upload (preview will use public URL)
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
        setVideoPreviewUrl(null);
      }

      setVideoUrl(publicUrl);
      setVideoFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      setErrors({ video: isArabic ? 'فشل تحميل الفيديو. يرجى المحاولة مرة أخرى.' : 'Failed to upload video. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    // Cleanup preview URL when removing
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl(null);
    }
    setVideoUrl('');
    setVideoFile(null);
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get preview source: use uploaded public URL if available, otherwise use local preview URL
  const previewSource = videoUrl || videoPreviewUrl;

  const handleSkip = async () => {
    await onSave({ intro_video_url: null });
    onNext({ intro_video_url: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Video is optional, so if no URL/file, just save null
    const videoData = { intro_video_url: videoUrl || null };
    
    await onSave(videoData);
    onNext(videoData);
  };


  return (
    <div>
      <h2 className="step-title">{t.video || 'Intro Video (Optional)'}</h2>
      <p className="step-subtitle">
        {t.videoSubtitle || 'A short intro video helps students choose you faster. Keep it 30-60 seconds.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        {/* Upload Video */}
        <div className="form-group">
          <label style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px', display: 'block' }}>
            {t.uploadVideo || 'Upload Intro Video'}
          </label>
          <span style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', display: 'block' }}>
            {t.videoSubtitle || 'A short intro video helps students choose you faster. Keep it 30-60 seconds.'}
          </span>
          
          {/* Upload Box */}
          <div 
            className={`upload-box ${previewSource ? 'has-file' : ''}`}
            onClick={() => !uploading && !previewSource && fileInputRef.current?.click()}
            style={{ 
              cursor: uploading || previewSource ? 'default' : 'pointer', 
              opacity: uploading ? 0.6 : 1,
              position: 'relative',
              minHeight: previewSource ? 'auto' : '200px'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/mov,video/quicktime,video/*"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 1 }}
            />
            
            {previewSource ? (
              <div style={{ 
                width: '100%', 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <video
                  src={previewSource}
                  controls
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '400px',
                    borderRadius: '12px',
                    backgroundColor: '#000',
                    marginBottom: '24px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
                {videoFile && !videoUrl && (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '12px', 
                    background: '#f0f7ff', 
                    borderRadius: '8px',
                    color: '#1e40af',
                    fontSize: '14px'
                  }}>
                    {isArabic ? 'جاري التحميل...' : uploading ? 'Uploading...' : 'Ready to upload'}
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  disabled={uploading}
                  style={{
                    padding: '10px 20px',
                    border: '2px solid #dc2626',
                    borderRadius: '10px',
                    background: 'white',
                    color: '#dc2626',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading) {
                      e.currentTarget.style.background = '#fee2e2';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <FaTimes /> {isArabic ? 'إزالة / تغيير' : 'Remove / Change'}
                </button>
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                <div style={{ 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaVideo style={{ 
                    fontSize: '56px', 
                    color: '#ff7a1a'
                  }} />
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  color: '#1f2937', 
                  marginBottom: '8px' 
                }}>
                  {isArabic ? 'انقر لتحميل الفيديو' : 'Click to upload video'}
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  color: '#6b7280',
                  marginBottom: '16px'
                }}>
                  {isArabic ? 'MP4، حد أقصى 50 ميجابايت، 60 ثانية' : 'MP4, max 50MB, 60 seconds'}
                </div>
                <div style={{
                  padding: '8px 16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#6b7280',
                  fontSize: '12px'
                }}>
                  {isArabic ? 'أو اسحب وأفلت الملف هنا' : 'or drag and drop file here'}
                </div>
              </div>
            )}
          </div>

          {/* Upload Status */}
          {uploading && (
            <div style={{ 
              marginTop: '16px', 
              padding: '12px 16px',
              background: '#f0f7ff',
              border: '2px solid #3b82f6',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#1e40af',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #3b82f6',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}></div>
              {isArabic ? 'جاري التحميل...' : 'Uploading video...'}
            </div>
          )}

          {videoUrl && !uploading && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px 16px',
              background: '#f0fdf4',
              border: '2px solid #22c55e',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#166534',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <FaCheckCircle style={{ color: '#10b981', fontSize: '16px' }} /> {isArabic ? 'تم تحميل الفيديو بنجاح' : 'Video uploaded successfully'}
            </div>
          )}
        </div>

        {/* Tips */}
        <div style={{
          padding: '16px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          marginTop: '16px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
            {t.tipsForVideo || 'Tips for a great intro video:'}
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#166534' }}>
            <li>{isArabic ? 'عرف بنفسك' : 'Introduce yourself'}</li>
            <li>{isArabic ? 'اذكر ما تدرسه' : 'Mention what you teach'}</li>
            <li>{isArabic ? 'اجعله 30-60 ثانية' : 'Keep it 30-60 seconds'}</li>
          </ul>
        </div>

        {errors.video && <span className="error-message">{errors.video}</span>}

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onBack({ intro_video_url: videoUrl || null })} disabled={uploading}>
            {t.back || 'Back'}
          </button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" className="btn btn-secondary" onClick={handleSkip} disabled={uploading}>
              {t.skipForNow || 'Skip for now'}
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? (isArabic ? 'جاري التحميل...' : 'Uploading...') : (t.saveAndContinue || 'Save and continue')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}